import { Controller } from '@hotwired/stimulus';

// What a row being carried looks like, and what stops the drag from selecting text instead -
// see the `is-dragging` / `is-sorting` rules in lineup_editor.css.
const DRAGGING = 'is-dragging';
const SORTING = 'is-sorting';

const ROW = '[data-sortable-target="row"]';
// Anything in the grab area that is a control in its own right: a pointerdown on the remove
// box is somebody ticking it, not somebody starting a drag.
const CONTROL = 'input, textarea, select, label';

// How close to the top or bottom of the window the pointer has to get before the page starts
// moving under it, and how fast it goes once it is right at the edge.
const EDGE = 72;
const MAX_SPEED = 18;

// Where a row sits vertically right now, transform included - which is the whole point during
// a drag: the carried row's middle is where the pointer has taken it, not where it started.
const middleOf = (row) => {
  const { top, height } = row.getBoundingClientRect();
  return top + height / 2;
};

// Reordering a list of form rows by dragging them, with the new order written into a hidden
// position field per row so a plain form submit carries it.
//
// Knows nothing about what it sorts: the line-up editor (#130) wires it, and so do the gear
// editor and the price list (#319), through profile_edits/_nested_rows.html.erb - a container,
// rows, a grab area each, and one position input each.
//
//   <div data-controller="sortable" data-sortable-ordinal-template-value="Slot %{number}"
//        data-sortable-position-offset-value="15">  <- optional starting position, default 0
//     <div data-sortable-target="row">
//       <div data-action="pointerdown->sortable#start">      <- the grab area
//         <button data-sortable-target="handle" class="hidden">⠿</button>
//         <span data-sortable-target="ordinal">1</span>
//       </div>
//       <input type="hidden" data-sortable-target="position" value="0">
//       <div data-sortable-target="fallback">↑ ↓ (server-side move buttons)</div>
//     </div>
//   </div>
//
// Progressive enhancement, the same bargain nested_form_controller makes: the server renders
// arrows that reorder through a normal request, and they are what works with no JS. This
// hides them, shows the handles, and makes the same change without a round trip.
//
// Pointer events rather than the HTML5 drag-and-drop API: that API does not fire on touch,
// and 90% of this app's traffic is a phone (CLAUDE.md).
//
// The move and release listeners go on the document, not on the thing that was grabbed. That
// is the whole trick: a pointer leaves a 32px handle after the first few pixels, and a
// `pointermove` bound to the handle simply stops arriving - which is what "you can grab it
// but not drag it" looks like. `setPointerCapture` is not enough on its own either, since the
// drag moves the captured element around the DOM and Chrome drops the capture when it does.
export default class extends Controller {
  static targets = ['row', 'handle', 'ordinal', 'position', 'fallback', 'status'];
  static values = {
    ordinalTemplate: String,
    announcementTemplate: String,
    positionOffset: { type: Number, default: 0 },
  };

  connect() {
    this.onMove = this.onMove.bind(this);
    this.onStop = this.onStop.bind(this);
    this.onScrollStep = this.onScrollStep.bind(this);
    this.blockNativeDrag = (event) => event.preventDefault();
  }

  disconnect() {
    this.finish();
  }

  // Swapped as each row connects, not in `connect()`: a row added after the fact (a
  // nested-form editor appends one live) goes through the same swap.
  handleTargetConnected(handle) {
    if (handle.closest(ROW)?.hasAttribute('data-sortable-fixed')) return;

    handle.classList.remove('hidden');
  }

  fallbackTargetConnected(fallback) {
    fallback.classList.add('hidden');
  }

  // A row appearing or leaving shifts everything below it. In the nested-form editors that is
  // an ordinary event - a blank row is appended as soon as the last one is typed into, and it
  // arrives carrying whatever number the <template> was rendered with.
  rowTargetConnected() {
    this.renumber();
  }

  rowTargetDisconnected() {
    this.renumber();
  }

  // Rows a drop can land on. A removed row is still in the form (it carries `_destroy`, which
  // is what tells the server to delete it) but it is not on screen, and its box is zero on
  // every side - left in, it would sit at the top of the window as far as the comparisons in
  // `settle` are concerned and act as a wall nothing could be dragged past.
  get visibleRows() {
    return this.rowTargets.filter((row) => row.getBoundingClientRect().height > 0);
  }

  get movableRows() {
    return this.visibleRows.filter((row) => !row.hasAttribute('data-sortable-fixed'));
  }

  // `pointerdown->sortable#start` on each row's grab area.
  start(event) {
    if (event.button > 0 || event.target.closest(CONTROL)) return;

    const row = event.target.closest(ROW);
    if (!row || row.hasAttribute('data-sortable-fixed')) return;

    // Stops the browser turning the gesture into a text selection or a scroll.
    event.preventDefault();
    this.dragging = row;
    this.originY = event.clientY;
    this.originMiddle = middleOf(row);
    this.lastY = event.clientY;
    row.classList.add(DRAGGING);
    this.element.classList.add(SORTING);

    document.addEventListener('pointermove', this.onMove, { passive: false });
    document.addEventListener('pointerup', this.onStop);
    document.addEventListener('pointercancel', this.onStop);
    // The browser's own drag-and-drop, which a pointer pressed on text starts on its own. It
    // takes the gesture over and fires `pointercancel` at us, so from the outside the row
    // simply refuses to move while the text under it lights up. Headless Chrome never shows
    // this: synthesised events do not start a native drag. The header also carries
    // `select-none`, which stops the selection that leads here in the first place.
    document.addEventListener('dragstart', this.blockNativeDrag);
  }

  onMove(event) {
    if (!this.dragging) return;

    event.preventDefault();
    this.lastY = event.clientY;
    this.drag();
    this.edgeScroll();
  }

  // The row follows the pointer. Without this the gesture has no feedback at all until the
  // swap happens, and on cards this tall that is most of a card's height away - which reads
  // exactly like "I grabbed it and nothing moved".
  drag() {
    this.dragging.style.transform = `translateY(${this.lastY - this.originY}px)`;
    this.settle();
  }

  // Swap with the neighbour the carried row has passed the middle of. Comparing middles (and
  // not "is the pointer over another row") is what makes a tall row swap as soon as it has
  // visibly moved past its neighbour, wherever inside it the pointer happens to be.
  settle() {
    const rows = this.movableRows;
    const index = rows.indexOf(this.dragging);
    const carried = this.originMiddle + (this.lastY - this.originY);
    const next = rows[index + 1];
    const previous = rows[index - 1];

    if (next && carried > middleOf(next)) this.place(next, 'afterend');
    else if (previous && carried < middleOf(previous)) this.place(previous, 'beforebegin');
  }

  // The row is really moved in the DOM, so the offset it was carrying is spent: it starts
  // following the pointer again from where it has just landed.
  place(neighbour, where) {
    neighbour.insertAdjacentElement(where, this.dragging);
    this.dragging.style.transform = '';
    this.originY = this.lastY;
    this.originMiddle = middleOf(this.dragging);
    this.renumber();
  }

  // A drag cannot reach past the window: `onMove` cancels the gesture's default, so the page
  // does not pan under it, and a list of thirty rows is several screens long. The edges scroll
  // instead, for as long as the pointer stays in them - held still at the bottom of the window
  // included, which is why this runs on a frame loop rather than on the next pointermove.
  edgeScroll() {
    this.scrollBy = this.edgeSpeed();
    if (this.scrollBy && !this.scrolling) this.scrolling = requestAnimationFrame(this.onScrollStep);
  }

  edgeSpeed() {
    const fromTop = this.lastY;
    const fromBottom = window.innerHeight - this.lastY;
    const ratio = (distance) => Math.min(1, Math.max(0, (EDGE - distance) / EDGE));

    if (fromTop < EDGE) return -Math.ceil(ratio(fromTop) * MAX_SPEED);
    if (fromBottom < EDGE) return Math.ceil(ratio(fromBottom) * MAX_SPEED);
    return 0;
  }

  onScrollStep() {
    this.scrolling = null;
    if (!this.dragging || !this.scrollBy) return;

    // How far the page ACTUALLY went, which is nothing once it is against either end - taking
    // the requested amount there would walk the row off on its own. The pointer has not moved,
    // so the row's starting point has to move by the same distance to keep it under the finger.
    const before = window.scrollY;
    window.scrollBy(0, this.scrollBy);
    const moved = window.scrollY - before;
    this.originY -= moved;

    this.drag();
    if (!moved) {
      this.scrollBy = 0;
      return;
    }
    this.scrolling = requestAnimationFrame(this.onScrollStep);
  }

  // The handle is a real keyboard control, not only a focusable pointer target. Arrow keys move
  // immediately - no hidden "grab mode" to discover - and the live region states the result.
  moveWithKeyboard(event) {
    const offset = { ArrowUp: -1, ArrowDown: 1 }[event.key];
    if (offset) this.moveBy(event, offset);
  }

  moveBy(event, offset) {
    const row = event.target.closest(ROW);
    const rows = this.movableRows;
    const index = rows.indexOf(row);
    // `index < 0` is not paranoia: a row this list does not hold gives -1, and -1 + 1 is a real
    // neighbour, so a pinned row would be answered with a move to the front of the list.
    const neighbour = index < 0 ? null : rows[index + offset];
    if (!row || !neighbour) return;

    event.preventDefault();
    event.stopPropagation();
    neighbour.insertAdjacentElement(offset < 0 ? 'beforebegin' : 'afterend', row);
    this.renumber();
    event.target.closest('[data-sortable-target="handle"]')?.focus();
    this.announce(row);
  }

  announce(row) {
    if (!this.hasStatusTarget || !this.hasAnnouncementTemplateValue) return;

    // Both halves out of the same list, or the sentence counts a row into the total that it
    // refuses to number - a pinned blank row is not somewhere this row could have gone.
    const rows = this.movableRows;
    this.statusTarget.textContent = this.announcementTemplateValue
      .replace('%{number}', rows.indexOf(row) + 1)
      .replace('%{count}', rows.length);
  }

  // A pristine nested-form row becomes sortable only after its first typed value. Until then it
  // remains pinned below every saved row and has no misleading drag handle.
  promote({ detail: { row } }) {
    row.removeAttribute('data-sortable-fixed');
    row.querySelector('[data-sortable-target="handle"]')?.classList.remove('hidden');
    this.renumber();
  }

  onStop() {
    this.finish();
    this.renumber();
  }

  finish() {
    if (this.scrolling) cancelAnimationFrame(this.scrolling);
    this.scrolling = null;
    this.scrollBy = 0;
    if (!this.dragging) return;

    this.dragging.style.transform = '';
    this.dragging.classList.remove(DRAGGING);
    this.element.classList.remove(SORTING);
    this.dragging = null;
    document.removeEventListener('pointermove', this.onMove);
    document.removeEventListener('pointerup', this.onStop);
    document.removeEventListener('pointercancel', this.onStop);
    document.removeEventListener('dragstart', this.blockNativeDrag);
  }

  // What the server reads. The visible "Slot 3" is renumbered alongside it - a row that keeps
  // its old number after being dragged reads as a drag that did not take. Removed rows are
  // skipped so the numbers left on screen run 1, 2, 3 rather than around a gap.
  renumber() {
    this.visibleRows.forEach((row, index) => {
      const position = row.querySelector('[data-sortable-target="position"]');
      if (position) position.value = index + this.positionOffsetValue;

      const ordinal = row.querySelector('[data-sortable-target="ordinal"]');
      if (ordinal && this.hasOrdinalTemplateValue) {
        ordinal.textContent = this.ordinalTemplateValue.replace('%{number}', index + 1);
      }
    });
  }
}
